/**
 * When 99.5% of the supply actually arrives.
 *
 * The only forward-looking page on this site, and exact rather than projected:
 * the runtime vests nothing before a grant's cliff, the whole grant from its
 * end, and linearly between, so summing that across the schedules is
 * arithmetic on chain state rather than a forecast.
 *
 * It also carries the evidence for a claim the network page makes. "Held in
 * the vesting pot" rests on an eight-byte `PalletId` spelling `qvesting`; here
 * the grants are shown summing to that account's balance, which is an
 * association no naming convention is doing any work in.
 */

import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

import type { VestingSummary } from '../api/generated/VestingSummary'
import { RequestFailed, fetchVesting } from '../api/rest'
import { height as fmtHeight, shortAddress, tokens, utcDay, utcMonth } from '../lib/format'
import { href } from '../lib/routes'
import { Field } from './Field'

const HEIGHT = 220
const PAD = { top: 18, right: 20, bottom: 30, left: 62 }
const FALLBACK_WIDTH = 720

function useMeasuredWidth(): [React.RefObject<HTMLDivElement | null>, number] {
  const ref = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(FALLBACK_WIDTH)
  useEffect(() => {
    const node = ref.current
    if (!node) return
    const observe = new ResizeObserver(([entry]) => {
      if (entry) setWidth(Math.max(320, entry.contentRect.width))
    })
    observe.observe(node)
    return () => observe.disconnect()
  }, [])
  return [ref, width]
}

const day = (iso: string) => utcDay(iso, true)
const month = utcMonth

/**
 * A raw amount as whole tokens, for geometry only.
 *
 * Every figure a reader sees goes through `tokens`, which is exact. A chain
 * amount is a 128-bit integer and `Number` loses it above 2^53, so this decides
 * how tall to draw a line and nothing else.
 */
const scaled = (raw: string, decimals: number) => {
  const n = Number(raw)
  return Number.isFinite(n) ? n / 10 ** decimals : 0
}

function ReleaseCurve({
  data,
  decimals,
  symbol,
}: {
  data: VestingSummary
  decimals: number
  symbol: string
}) {
  const [wrap, width] = useMeasuredWidth()
  const points = data.release
  if (points.length < 2) return null

  const times = points.map((p) => new Date(p.at).getTime())
  const t0 = Math.min(...times)
  const t1 = Math.max(...times)
  const span = t1 - t0 || 1
  const peak = Math.max(...points.map((p) => scaled(p.vested, decimals)), 1)
  const top = peak * 1.08

  const plotW = width - PAD.left - PAD.right
  const plotH = HEIGHT - PAD.top - PAD.bottom
  const baseline = PAD.top + plotH
  const x = (t: number) => PAD.left + ((t - t0) / span) * plotW
  const y = (v: number) => baseline - (v / top) * plotH

  const placed = points.map((p, i) => ({
    x: x(times[i] ?? t0),
    y: y(scaled(p.vested, decimals)),
  }))
  const line = placed.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)} ${p.y.toFixed(1)}`)
  const first = placed[0]!
  const last = placed[placed.length - 1]!
  const area = `${line.join(' ')} L${last.x.toFixed(1)} ${baseline} L${first.x.toFixed(1)} ${baseline} Z`

  const nowX = x(new Date(data.computed_at).getTime())
  const yTicks = [0, top / 2, top].map((v) => ({ v, y: y(v) }))
  const xTicks = [0, 0.5, 1].map((f) => ({ t: t0 + span * f, x: x(t0 + span * f) }))

  return (
    <div ref={wrap}>
      <svg
        className="chart"
        width={width}
        height={HEIGHT}
        viewBox={`0 0 ${width} ${HEIGHT}`}
        role="img"
        aria-label={`Cumulative vested supply from ${day(points[0]!.at)} to ${day(
          points[points.length - 1]!.at,
        )}, reaching ${tokens(data.total_scheduled, decimals, 0)} ${symbol}.`}
      >
        {yTicks.map((t) => (
          <g key={t.v}>
            <line className="grid" x1={PAD.left} x2={width - PAD.right} y1={t.y} y2={t.y} />
            <text className="chart-axis" x={PAD.left - 8} y={t.y + 3.5} textAnchor="end">
              {t.v === 0 ? '0' : `${Math.round(t.v / 1000)}k`}
            </text>
          </g>
        ))}
        {xTicks.map((t, i) => (
          <text
            key={t.x}
            className="chart-axis"
            x={t.x}
            y={HEIGHT - 8}
            textAnchor={i === 0 ? 'start' : i === 2 ? 'end' : 'middle'}
          >
            {month(new Date(t.t).toISOString())}
          </text>
        ))}

        <path className="area" d={area} />
        <path className="line" d={line.join(' ')} />

        {/* Where the reader is standing. On this chain that is flat against the
            floor a year before anything unlocks, which is the single most
            useful thing the curve has to say. */}
        {nowX >= PAD.left && nowX <= width - PAD.right && (
          <>
            <line className="crosshair" x1={nowX} x2={nowX} y1={PAD.top} y2={baseline} />
            <text
              className="chart-axis"
              x={nowX + 5}
              y={PAD.top + 9}
              style={{ fill: 'var(--data-bright)' }}
            >
              now
            </text>
          </>
        )}
      </svg>
    </div>
  )
}

export function VestingPanel({
  chain,
  decimals,
  symbol,
}: {
  chain: string
  decimals: number
  symbol: string
}) {
  const [data, setData] = useState<VestingSummary | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    setData(null)
    setError(null)
    fetchVesting(chain, controller.signal)
      .then(setData)
      .catch((e: unknown) => {
        if (controller.signal.aborted) return
        setError(e instanceof RequestFailed ? e.message : 'Could not reach the observer.')
      })
    return () => controller.abort()
  }, [chain])

  if (error) return <p className="empty">{error}</p>
  if (!data) return <p className="empty">Reading the schedules…</p>
  if (data.schedules.length === 0) {
    return <p className="empty">This chain has no vesting schedules.</p>
  }

  const amount = (raw: string) => `${tokens(raw, decimals)} ${symbol}`
  const outstanding = (() => {
    try {
      return (BigInt(data.total_scheduled) - BigInt(data.total_claimed)).toString()
    } catch {
      return null
    }
  })()
  // Vested and not yet taken, summed across every grant. The headline figure on
  // this chain today: two grants began vesting at launch while the other
  // forty-six wait for 2027, so a page that only said "first claimable" would
  // miss that a great deal already is.
  const claimableNow = (() => {
    try {
      return data.schedules.reduce((sum, s) => sum + BigInt(s.claimable), 0n).toString()
    } catch {
      return null
    }
  })()

  const firstCliff = data.schedules.reduce(
    (a, s) => (a === null || s.cliff < a ? s.cliff : a),
    null as string | null,
  )
  const lastEnd = data.schedules.reduce(
    (a, s) => (a === null || s.end > a ? s.end : a),
    null as string | null,
  )

  return (
    <>
      <section className="panel" style={{ marginBottom: 26 }}>
        <div className="panel-head">
          <h2 className="panel-title">Vesting</h2>
          <span className="eyebrow">read from chain state</span>
        </div>
        <div className="node-grid">
          <Field label="Scheduled" value={amount(data.total_scheduled)} note="across every grant" />
          <Field label="Claimed" value={amount(data.total_claimed)} note="taken so far" />
          <Field
            label="Still to come"
            value={outstanding === null ? '—' : amount(outstanding)}
            note="scheduled less claimed"
          />
          <Field
            label="Claimable now"
            value={claimableNow === null ? '—' : amount(claimableNow)}
            note="vested and not yet taken"
          />
          <Field label="Grants" value={fmtHeight(data.schedules.length)} />
          <Field
            label="First claimable"
            value={firstCliff === null ? '—' : day(firstCliff)}
            note="nothing unlocks before this"
          />
          <Field label="Fully vested" value={lastEnd === null ? '—' : day(lastEnd)} />
        </div>
        <p className="panel-note">
          {data.matches_pot ? (
            <>
              <strong>The grants account for the pot exactly.</strong> They sum to{' '}
              {amount(data.total_scheduled)} against the{' '}
              {data.pot?.balance ? amount(data.pot.balance) : 'balance'} held at{' '}
              {data.pot ? <code>{shortAddress(data.pot.address)}</code> : 'the pallet account'} —
              the difference is the existential deposit, the minimum that keeps the account from
              being reaped. That match is why the network page can call it a vesting pot: the
              association is arithmetic, not an inference from the eight bytes of{' '}
              <code>PalletId</code> that happen to spell <code>qvesting</code>.
            </>
          ) : (
            <>
              <strong>The grants no longer account for the pot.</strong> They sum to{' '}
              {amount(data.total_scheduled)} against{' '}
              {data.pot?.balance ? amount(data.pot.balance) : 'an unknown balance'}, a difference of{' '}
              {amount(data.pot_shortfall)}. A grant ended or the pot topped up would both do this;
              until it is understood, treat the two as separate facts.
            </>
          )}
        </p>
      </section>

      <section className="panel" style={{ marginBottom: 26 }}>
        <div className="panel-head">
          <h2 className="panel-title">When it arrives</h2>
          <span className="eyebrow">cumulative, in {symbol}</span>
        </div>
        <ReleaseCurve data={data} decimals={decimals} symbol={symbol} />
        <p className="panel-note">
          Exact rather than projected. The runtime vests nothing before a grant&apos;s cliff, the
          whole grant from its end, and linearly in between — so this is that arithmetic summed
          across every schedule, not a model of what somebody intends.
        </p>
        <p className="panel-note">
          The step at the left is not the whole story told small:{' '}
          <strong>the grants do not share a cliff</strong>. Read the table for which ones have
          started — a curve that rises early and then sits flat for a year is two grants vesting
          from launch while the rest wait, not a gentle beginning.
        </p>
      </section>

      <section className="panel" style={{ marginBottom: 26 }}>
        <div className="panel-head">
          <h2 className="panel-title">Every grant</h2>
          <span className="eyebrow">largest first · amounts in {symbol}</span>
        </div>
        <div className="scroll-x">
          <table className="board">
            <caption className="visually-hidden">Vesting grants, largest first</caption>
            <thead>
              <tr>
                <th scope="col" className="left">
                  Beneficiary
                </th>
                <th scope="col">Grant</th>
                <th scope="col">Vested</th>
                <th scope="col">Claimable</th>
                <th scope="col">Claimed</th>
                <th scope="col" className="left">
                  Unlocks
                </th>
                <th scope="col" className="left">
                  Fully vested
                </th>
              </tr>
            </thead>
            <tbody>
              {data.schedules.map((s) => (
                <tr key={s.id}>
                  <td className="left">
                    <Link to={href({ chain, account: s.beneficiary })} title={s.beneficiary}>
                      {shortAddress(s.beneficiary)}
                    </Link>
                  </td>
                  <td className="numeral">{tokens(s.total, decimals, 0)}</td>
                  <td className="numeral">{tokens(s.vested, decimals, 0)}</td>
                  <td className="numeral">{tokens(s.claimable, decimals, 0)}</td>
                  <td className="numeral">{tokens(s.claimed, decimals, 0)}</td>
                  <td className="left">{day(s.cliff)}</td>
                  <td className="left">{day(s.end)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="panel-note">
          <strong>A snapshot, never a promise.</strong> <code>Vesting::retarget_schedule</code> can
          change who a grant pays and <code>end_schedule</code> can end one, so every row describes
          what the chain says today rather than what anybody is owed.
        </p>
        <p className="panel-note">
          The beneficiaries are addresses and nothing more. Nothing on this chain calls anyone a
          founder, a team or an investor, so neither does this table — the only thing asserted here
          is which address a grant is currently pointed at.
        </p>
      </section>
    </>
  )
}
